import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import ActiveSessionBanner from './ActiveSessionBanner';
import { SESSION_STATUS } from '../constants';

const SessionBannerManager = memo(({
  activeSession,
  dismissedSessionId,
  currentView,
  onDismiss,
  onBannerHeightChange
}) => {
  const bannerRef = useRef(null);

  const showActiveBanner = activeSession?.status === SESSION_STATUS.ACTIVE && currentView !== 'session';
  const showPlannedBanner = activeSession?.status === SESSION_STATUS.PLANNED
    && currentView !== 'session'
    && activeSession?.id !== dismissedSessionId;
  const shouldShowBanner = showActiveBanner || showPlannedBanner;

  // Banner-Hoehe messen und an Parent melden
  useLayoutEffect(() => {
    if (!shouldShowBanner) {
      onBannerHeightChange(0);
      return;
    }
    if (!bannerRef.current) return;
    const nextHeight = bannerRef.current.offsetHeight;
    onBannerHeightChange(nextHeight);
  }, [shouldShowBanner, activeSession?.status, dismissedSessionId, currentView, onBannerHeightChange]);

  // Resize-Listener
  useEffect(() => {
    if (!shouldShowBanner) return undefined;
    const handleResize = () => {
      if (bannerRef.current) {
        onBannerHeightChange(bannerRef.current.offsetHeight);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [shouldShowBanner, onBannerHeightChange]);

  if (!shouldShowBanner) return null;

  return (
    <>
      {/* Active Session Banner - immer sichtbar, nicht dismissbar */}
      {showActiveBanner && (
        <div
          ref={bannerRef}
          className="fixed top-0 left-0 right-0 z-[60]"
          style={{ top: 'var(--native-safe-area-top, env(safe-area-inset-top, 0px))' }}
        >
          <ActiveSessionBanner activeSession={activeSession} />
        </div>
      )}
      {/* Planned Session Banner - nur <5min vor Start, dismissbar */}
      {showPlannedBanner && (
        <div
          ref={bannerRef}
          className="fixed top-0 left-0 right-0 z-[60]"
          style={{ top: 'var(--native-safe-area-top, env(safe-area-inset-top, 0px))' }}
        >
          <ActiveSessionBanner
            activeSession={activeSession}
            onDismiss={onDismiss}
          />
        </div>
      )}
    </>
  );
});

SessionBannerManager.displayName = 'SessionBannerManager';

SessionBannerManager.propTypes = {
  activeSession: PropTypes.object,
  dismissedSessionId: PropTypes.string,
  currentView: PropTypes.string.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onBannerHeightChange: PropTypes.func.isRequired
};

export default SessionBannerManager;

// Export fuer MainScreen: ob ein Banner sichtbar ist
export const useShouldShowBanner = (activeSession, currentView, dismissedSessionId) => {
  const showActiveBanner = activeSession?.status === SESSION_STATUS.ACTIVE && currentView !== 'session';
  const showPlannedBanner = activeSession?.status === SESSION_STATUS.PLANNED
    && currentView !== 'session'
    && activeSession?.id !== dismissedSessionId;
  return showActiveBanner || showPlannedBanner;
};
